/**
 * Named editorial author for the article library.
 *
 * Bylines and BlogPosting structured data point at one real person so the
 * search engines can attach the legal/energy expertise behind the guides to
 * a verifiable entity, instead of an anonymous site credit.
 */

export type Author = {
  slug: string;
  name: string;
  /** Short byline form used under article headlines. */
  byline: string;
  jobTitle: string;
  credential: string;
  /** Official bar/licensing record used for Person.sameAs and verified on the bio page. */
  barProfile?: { label: string; url: string };
  path: string;
  summary: string;
  bio: string[];
  /** Scope-of-license notice shown wherever the advisory role is claimed. */
  jurisdictionNote: string;
  /** How the library is produced and what the advisory role does and does not cover. */
  roleNote: string;
  focusAreas: string[];
  knowsAbout: string[];
};

export const JOSHUA_COLE: Author = {
  slug: "joshua-cole",
  name: "Joshua Cole",
  byline: "Joshua Cole, Attorney (ND)",
  jobTitle: "Legal editorial advisor and land use specialist",
  credential: "Licensed North Dakota attorney",
  barProfile: {
    label: "State Bar of North Dakota lawyer record",
    url: "https://www.ndcourts.gov/lawyers/06028",
  },
  path: "/editorial/joshua-cole",
  summary:
    "Licensed North Dakota attorney with two decades of specialized experience in Bakken energy development and land use, serving as legal editorial advisor to the Easements.com research library.",
  bio: [
    "Joshua Cole is a licensed North Dakota attorney with two decades of specialized experience in Bakken energy development and land use.",
    "He advises developers, landowners, and infrastructure companies through complex right-of-way acquisitions, easement agreements, leasing, and regulatory compliance.",
    "That work sits on both sides of the same table: negotiating corridor terms for the company that needs the route, and reading the same instruments for the landowner whose acreage carries it. He serves as legal editorial advisor to this site, setting the sourcing standards the research desk writes to. Recorded documents, the state code, and the county indexes where these rights actually live.",
  ],
  jurisdictionNote:
    "Joshua Cole is admitted to practice law in the State of North Dakota. Material on this site that addresses the law of other jurisdictions is published as general educational research only, and is not legal practice, representation, or local counsel in those states.",
  roleNote:
    "Articles and guides on Easements.com are compiled by the Easements.com research desk using automated research tools together with primary sources: state statutes, published court opinions, county recorder publications and public GIS records. Joshua Cole serves as legal editorial advisor, setting the sourcing and accuracy standards the library is written to. That advisory role is not an individualized legal review of any article for your property, and reading this site does not create an attorney-client relationship.",
  focusAreas: [
    "Right-of-way acquisition and corridor routing",
    "Pipeline, gathering-line, and electric transmission easements",
    "Oil, gas, wind, and solar leasing",
    "Surface use and damage agreements",
    "Title review, easement defects, and curative work",
    "State and federal regulatory compliance for energy infrastructure",
  ],
  knowsAbout: [
    "Easement law",
    "Right-of-way acquisition",
    "Bakken energy development",
    "Oil and gas leasing",
    "Land use regulation",
    "Title examination",
    "Real property law",
  ],
};

/** Every author with a public profile page. */
export const AUTHORS: Author[] = [JOSHUA_COLE];

export function findAuthor(slug: string): Author | undefined {
  return AUTHORS.find((author) => author.slug === slug);
}
